import type { Lane } from "@szyos/contracts";
import type { FlowModel, NodeStatus } from "@szyos/domain";
import {
  Background,
  BackgroundVariant,
  Controls,
  Handle,
  Position,
  ReactFlow,
  getBezierPath,
  type Edge,
  type EdgeProps,
  type Node,
  type NodeProps,
} from "@xyflow/react";
import "@xyflow/react/dist/base.css";
import { useMemo } from "react";
import { LANE_ICON, adapterLabel } from "../format";
import { Icon } from "../icons";

export type FlowTarget = { kind: "lane"; lane: Lane } | { kind: "stage"; stage: StageKey };
type StageKey = "brief" | "order" | "orch" | "gate" | "pr" | "staging" | "prod" | "live";
type Accent = "violet" | "lime" | "orange" | "cyan" | "coral" | "amber";

const LANE_ACCENT: Record<Lane, Accent> = {
  research: "lime",
  design: "orange",
  code: "cyan",
  test: "coral",
  docs: "violet",
};
const STATUS_TEXT: Record<NodeStatus, string> = {
  done: "verifiziert",
  run: "läuft",
  wait: "wartet",
  block: "blockiert",
  gate: "Freigabe nötig",
};
const PAUSED_TEXT = "Provider pausiert";

interface StageData extends Record<string, unknown> {
  title: string;
  sub: string;
  sub2?: string;
  icon: string;
  accent: Accent;
  status: NodeStatus;
  corner?: boolean;
  big?: boolean;
  /** Anbieterpause oder Anhalten: eigener Status statt „wartet“, damit es nicht wie Leerlauf aussieht. */
  paused?: boolean;
  pausedText?: string;
  onOpen: () => void;
}
interface LaneData extends Record<string, unknown> {
  title: string;
  lane: Lane;
  done: number;
  total: number;
  providers: string[];
  status: NodeStatus;
  onOpen: () => void;
}
interface BriefData extends Record<string, unknown> {
  title: string;
  goal: string;
  meta: string;
  tags: string[];
}

const accentStyle = (accent: Accent): React.CSSProperties =>
  ({
    "--accent": `var(--${accent})`,
    "--accent-a": `color-mix(in srgb, var(--${accent}) 50%, transparent)`,
  }) as React.CSSProperties;

function StatusBadge({
  status,
  corner,
  paused,
  pausedText = PAUSED_TEXT,
}: {
  status: NodeStatus;
  corner?: boolean;
  paused?: boolean;
  pausedText?: string;
}) {
  if (paused) {
    return (
      <span className={`node-status is-paused${corner ? " is-corner" : ""}`} title={pausedText}>
        <Icon name="clock" />
      </span>
    );
  }
  const icon = status === "done" ? "check" : status === "block" || status === "gate" ? "alert" : null;
  return (
    <span className={`node-status is-${status}${corner ? " is-corner" : ""}`} title={STATUS_TEXT[status]}>
      {icon && <Icon name={icon} />}
    </span>
  );
}

function Handles() {
  return (
    <>
      <Handle type="target" position={Position.Left} id="l" className="fm-handle" isConnectable={false} />
      <Handle type="source" position={Position.Right} id="r" className="fm-handle" isConnectable={false} />
      <Handle type="target" position={Position.Top} id="t" className="fm-handle" isConnectable={false} />
      <Handle type="source" position={Position.Bottom} id="b" className="fm-handle" isConnectable={false} />
    </>
  );
}

function StageNode({ data }: NodeProps<Node<StageData>>) {
  const d = data;
  const classes = [
    "node",
    "fm-node",
    d.big && "is-big",
    d.status === "gate" && "is-gate-node",
    d.status === "wait" && !d.paused && "is-dim",
    d.paused && "is-paused-node",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <button type="button" className={classes} style={accentStyle(d.accent)} onClick={d.onOpen}>
      <Handles />
      <span className="node-icon">
        <Icon name={d.icon} />
      </span>
      <span className="node-body">
        <span className="node-title">{d.title}</span>
        <span className="node-sub">{d.sub}</span>
        {d.sub2 && <span className="node-sub mono">{d.sub2}</span>}
      </span>
      <StatusBadge status={d.status} corner={d.corner} paused={d.paused} pausedText={d.pausedText} />
      <span className="sr-only">
        , {d.paused ? (d.pausedText ?? PAUSED_TEXT) : STATUS_TEXT[d.status]}. Details öffnen
      </span>
    </button>
  );
}

function LaneNode({ data }: NodeProps<Node<LaneData>>) {
  const d = data;
  const accent = LANE_ACCENT[d.lane];
  return (
    <button type="button" className="node fm-node lane" style={accentStyle(accent)} onClick={d.onOpen}>
      <Handles />
      <span className="node-icon">
        <Icon name={LANE_ICON[d.lane]} />
      </span>
      <span className="node-body">
        <span className="node-title">{d.title}</span>
        <span className="node-sub">
          {d.done}/{d.total} Aufgaben
        </span>
      </span>
      <StatusBadge status={d.status} />
      <span className="node-more" aria-hidden="true">
        <Icon name="more" />
      </span>
      <span className="lane-bar">
        <span className="bar">
          <i style={{ ["--w" as string]: `${d.total ? (d.done / d.total) * 100 : 0}%` }} />
        </span>
        <span className="provider">{d.providers.map(adapterLabel).join(" · ")}</span>
      </span>
      <span className="sr-only">, {STATUS_TEXT[d.status]}. Details öffnen</span>
    </button>
  );
}

function GateNode({ data }: NodeProps<Node<StageData>>) {
  const d = data;
  return (
    <button type="button" className={`gate fm-gate is-${d.status}`} onClick={d.onOpen}>
      <Handles />
      <span className="gate-inner">
        <Icon name="shield" className="gate-icon" />
        <strong>QUALITY GATE</strong>
        <small>CI · Security · Review</small>
        <small>{d.sub}</small>
      </span>
      <span className="sr-only">, {STATUS_TEXT[d.status]}. Details öffnen</span>
    </button>
  );
}

function BriefNode({ data }: NodeProps<Node<BriefData>>) {
  return (
    <article className="brief fm-brief">
      <Handles />
      <div className="brief-icon">
        <Icon name="file" />
      </div>
      <div>
        <h3>{data.title}</h3>
        <p className="brief-meta mono">{data.meta}</p>
        <p>{data.goal}</p>
        {data.tags.length > 0 && (
          <ul className="tags">
            {data.tags.map((t) => (
              <li key={t}>{t}</li>
            ))}
          </ul>
        )}
      </div>
    </article>
  );
}

type EdgeState = "done" | "flow" | "wait" | "block";
interface GlowData extends Record<string, unknown> {
  from: Accent;
  to: Accent;
  state: EdgeState;
  vertical?: boolean;
}

const reduceMotion =
  typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function GlowEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
}: EdgeProps<Edge<GlowData>>) {
  const d = data ?? { from: "violet", to: "violet", state: "wait" };
  const path = d.vertical
    ? `M ${sourceX} ${sourceY} L ${targetX} ${targetY}`
    : getBezierPath({
        sourceX,
        sourceY,
        targetX,
        targetY,
        sourcePosition,
        targetPosition,
        curvature: 0.45,
      })[0];
  const c1 = d.state === "wait" ? "var(--st-wait)" : `var(--${d.from})`;
  const c2 = d.state === "wait" ? "var(--st-wait)" : d.state === "block" ? "var(--red)" : `var(--${d.to})`;
  const gid = `g-${id}`;
  const dashed = d.state === "wait" || d.state === "block";
  return (
    <g>
      <defs>
        <linearGradient
          id={gid}
          gradientUnits="userSpaceOnUse"
          x1={sourceX}
          y1={sourceY}
          x2={targetX + 0.01}
          y2={targetY + 0.01}
        >
          <stop offset="0" style={{ stopColor: c1 }} />
          <stop offset="1" style={{ stopColor: c2 }} />
        </linearGradient>
      </defs>
      {!dashed && <path d={path} className="edge-glow" stroke={`url(#${gid})`} />}
      <path d={path} className={`edge${dashed ? " is-wait" : ""}`} stroke={`url(#${gid})`} />
      <circle cx={sourceX} cy={sourceY} r={3.2} style={{ fill: c1 }} />
      {d.vertical ? (
        <path
          d={`M ${targetX - 6} ${targetY - 8} L ${targetX} ${targetY} L ${targetX + 6} ${targetY - 8} Z`}
          style={{ fill: c2 }}
        />
      ) : (
        <circle cx={targetX} cy={targetY} r={3.2} style={{ fill: c2 }} />
      )}
      {d.state === "flow" &&
        !reduceMotion &&
        [0, 1, 2].map((i) => (
          <g key={i}>
            <circle r={3.4} fill="#fff" opacity={0.9} className="particle-glow">
              <animateMotion
                dur={d.vertical ? "1.6s" : "2.4s"}
                begin={`${-i * 0.8}s`}
                repeatCount="indefinite"
                path={path}
              />
            </circle>
            <circle r={1.8} style={{ fill: c2 }}>
              <animateMotion
                dur={d.vertical ? "1.6s" : "2.4s"}
                begin={`${-i * 0.8}s`}
                repeatCount="indefinite"
                path={path}
              />
            </circle>
          </g>
        ))}
    </g>
  );
}

const nodeTypes = { stage: StageNode, lane: LaneNode, gate: GateNode, brief: BriefNode };
const edgeTypes = { glow: GlowEdge };

function edgeState(from: NodeStatus, to: NodeStatus): EdgeState {
  if (to === "block") return "block";
  if (to === "run" || to === "gate") return from === "wait" ? "wait" : "flow";
  if (to === "done") return "done";
  return from === "done" || from === "run" ? "wait" : "wait";
}

export interface FlowMapProps {
  flow: FlowModel;
  title: string;
  goal: string;
  meta: string;
  tags: string[];
  /** Bei `PAUSED_PROVIDER`: kurzer Text zur Rücksetzzeit, sonst `null`. */
  paused?: string | null;
  /** Stufe „Live“: echter Stand nach dem Merge (packages/domain/src/live.ts); ohne Angabe kein Knoten. */
  live?: { status: NodeStatus; sub: string } | null;
  onOpen: (target: FlowTarget) => void;
}

/** Flow Map des Auftrags (docs/07 §2, docs/16 §6) mit @xyflow/react. */
export function FlowMap({ flow, title, goal, meta, tags, paused = null, live = null, onOpen }: FlowMapProps) {
  const { nodes, edges } = useMemo(() => {
    const orchestrator: NodeStatus = paused ? "wait" : flow.orchestrator;
    // Angehalten und kein Lauf mehr aktiv: Pause zeigen statt „koordiniert“.
    const held = !paused && flow.held && orchestrator !== "run";
    const n = Math.max(1, flow.lanes.length);
    const laneBlock = n * 102;
    const cy = Math.max(laneBlock / 2 + 20, 250);
    const laneY = (i: number) => cy - laneBlock / 2 + i * 102 + 7;
    const open = (target: FlowTarget) => () => onOpen(target);

    const nodes: Node[] = [
      {
        id: "brief",
        type: "brief",
        position: { x: 0, y: 0 },
        data: { title, goal, meta, tags } satisfies BriefData,
        selectable: false,
      },
      {
        id: "order",
        type: "stage",
        position: { x: 0, y: cy - 36 },
        data: {
          title: "Auftrag",
          sub: flow.briefing === "done" ? "Spec freigegeben" : "Briefing",
          icon: "file",
          accent: "violet",
          status: flow.briefing,
          corner: true,
          onOpen: open({ kind: "stage", stage: "order" }),
        } satisfies StageData,
      },
      {
        id: "orch",
        type: "stage",
        position: { x: 184, y: cy - 50 },
        data: {
          title: "Orchestrator",
          sub: paused
            ? "Provider pausiert"
            : held
              ? "Angehalten"
              : flow.orchestrator === "run"
                ? "Koordiniert Läufe"
                : "Plant · koordiniert",
          sub2:
            paused ??
            (held
              ? "keine neuen Läufe"
              : flow.orchestrator === "wait"
                ? flow.lanes.length > 0
                  ? "kein Lauf aktiv"
                  : "Plan offen"
                : "DAG geprüft"),
          icon: "network",
          accent: "violet",
          status: orchestrator,
          corner: true,
          big: true,
          paused: Boolean(paused) || held,
          pausedText: held ? "Angehalten" : PAUSED_TEXT,
          onOpen: open({ kind: "stage", stage: "orch" }),
        } satisfies StageData,
      },
      ...flow.lanes.map((l, i): Node => ({
        id: `lane-${l.lane}`,
        type: "lane",
        position: { x: 441, y: laneY(i) },
        data: {
          title: l.label,
          lane: l.lane,
          done: l.done,
          total: l.total,
          providers: l.providers,
          status: l.status,
          onOpen: open({ kind: "lane", lane: l.lane }),
        } satisfies LaneData,
      })),
      {
        id: "gate",
        type: "gate",
        position: { x: 737, y: cy - 75 },
        data: {
          title: "Quality Gate",
          sub: flow.gate === "done" ? "bestanden" : flow.gate === "run" ? "läuft" : "wartet",
          icon: "shield",
          accent: "violet",
          status: flow.gate,
          onOpen: open({ kind: "stage", stage: "gate" }),
        } satisfies StageData,
      },
      {
        id: "pr",
        type: "stage",
        position: { x: 918, y: cy - 118 },
        data: {
          title: "Review",
          sub:
            flow.pr === "gate"
              ? "wartet auf Review"
              : flow.pr === "done"
                ? "angenommen"
                : "noch nicht erreicht",
          icon: "pr",
          accent: "violet",
          status: flow.pr,
          onOpen: open({ kind: "stage", stage: "pr" }),
        } satisfies StageData,
      },
      {
        id: "staging",
        type: "stage",
        position: { x: 912, y: cy + 6 },
        data: {
          title: "Vor dem Mergen",
          sub:
            flow.staging === "done"
              ? "Stand und CI geprüft"
              : flow.staging === "run"
                ? "prüft Stand und CI"
                : "noch nicht erreicht",
          icon: "server",
          accent: "cyan",
          status: flow.staging,
          onOpen: open({ kind: "stage", stage: "staging" }),
        } satisfies StageData,
      },
      {
        id: "prod",
        type: "stage",
        position: { x: 890, y: cy + 130 },
        data: {
          title: "Auslieferung",
          sub:
            flow.production === "gate"
              ? "Freigabe ausstehend"
              : flow.production === "done"
                ? "ausgeliefert"
                : "Gate nicht erreicht",
          icon: "rocket",
          accent: "amber",
          status: flow.production,
          onOpen: open({ kind: "stage", stage: "prod" }),
        } satisfies StageData,
      },
      ...(live
        ? [
            {
              id: "live",
              type: "stage",
              position: { x: 868, y: cy + 254 },
              data: {
                title: "Live",
                sub: live.sub,
                icon: "activity",
                accent: "lime",
                status: live.status,
                onOpen: open({ kind: "stage", stage: "live" }),
              } satisfies StageData,
            },
          ]
        : []),
    ];

    const e = (
      id: string,
      source: string,
      target: string,
      data: GlowData,
      vertical = false,
    ): Edge<GlowData> => ({
      id,
      source,
      target,
      type: "glow",
      sourceHandle: vertical ? "b" : "r",
      targetHandle: vertical ? "t" : "l",
      data: { ...data, vertical },
      selectable: false,
      focusable: false,
    });
    const edges: Edge<GlowData>[] = [
      e("order-orch", "order", "orch", {
        from: "violet",
        to: "violet",
        state: edgeState(flow.briefing, orchestrator),
      }),
      ...flow.lanes.flatMap((l) => [
        e(`orch-${l.lane}`, "orch", `lane-${l.lane}`, {
          from: "violet",
          to: LANE_ACCENT[l.lane],
          state: edgeState(orchestrator, l.status),
        }),
        e(`${l.lane}-gate`, `lane-${l.lane}`, "gate", {
          from: LANE_ACCENT[l.lane],
          to: "violet",
          state:
            l.status === "done"
              ? flow.gate === "run"
                ? "flow"
                : "done"
              : l.status === "run"
                ? "flow"
                : "wait",
        }),
      ]),
      e("gate-pr", "gate", "pr", { from: "violet", to: "violet", state: edgeState(flow.gate, flow.pr) }),
      e(
        "pr-staging",
        "pr",
        "staging",
        { from: "violet", to: "cyan", state: edgeState(flow.pr, flow.staging) },
        true,
      ),
      e(
        "staging-prod",
        "staging",
        "prod",
        { from: "cyan", to: "amber", state: edgeState(flow.staging, flow.production) },
        true,
      ),
      ...(live
        ? [
            e(
              "prod-live",
              "prod",
              "live",
              { from: "amber", to: "lime", state: edgeState(flow.production, live.status) },
              true,
            ),
          ]
        : []),
    ];
    return { nodes, edges };
  }, [flow, title, goal, meta, tags, paused, live, onOpen]);

  return (
    <div className="fm-canvas">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        fitViewOptions={{ padding: 0.1 }}
        minZoom={0.35}
        maxZoom={1.25}
        nodesDraggable={false}
        nodesConnectable={false}
        nodesFocusable={false}
        edgesFocusable={false}
        elementsSelectable={false}
        zoomOnScroll={false}
        panOnScroll={false}
        preventScrolling={false}
        proOptions={{ hideAttribution: false }}
      >
        <Background variant={BackgroundVariant.Dots} gap={22} size={1.2} className="fm-bg" />
        <Controls
          showInteractive={false}
          position="bottom-left"
          orientation="horizontal"
          className="fm-controls"
          aria-label="Zoom der Flow Map"
        />
      </ReactFlow>
    </div>
  );
}
